// Mein Konto (M05 erweitert): Profil (Name, Sprache), Passwort ändern, Zwei-Faktor-Anmeldung, angemeldete Geräte.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ak, deviceLabel, disableMfa, serverMessage, useMfaStatus, useSessions, type SessionRow } from '../../api/account';
import { api } from '../../api/client';
import { qk, useMe } from '../../api/queries';
import type { Me } from '../../api/types';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/toast';
import { Dialog, Field, formatDateTime, PageHead, relativeTime, Spinner } from '../../components/ui';
import { localeOf, setLanguage } from '../../i18n';
import { CodeInput, MfaSetupWizard, RecoveryCodes } from './MfaSetup';
import { PasswordFields, passwordOk } from './PasswordFields';
import '../../styles/account.css';

export function AccountPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  if (!me) return <Spinner />;
  return (
    <>
      <PageHead title={t('account.title')} sub={me.user.email} />
      <div className="account-grid">
        <ProfileCard me={me} />
        <PasswordCard />
        <MfaCard me={me} />
        <SessionsCard />
      </div>
    </>
  );
}

function ProfileCard({ me }: { me: Me }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(me.user.displayName);
  const [locale, setLocale] = useState<string>(me.user.locale ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(me.user.displayName);
    setLocale(me.user.locale ?? '');
  }, [me.user.displayName, me.user.locale]);
  const dirty = name.trim() !== me.user.displayName || locale !== (me.user.locale ?? '');
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const next = await api.patch<Me>('/auth/profile', { displayName: name.trim(), locale: locale === '' ? null : locale });
      qc.setQueryData(qk.me, next);
      if (locale === 'de' || locale === 'en') setLanguage(locale);
      toast.show(t('common.saved'));
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card col" onSubmit={save} aria-labelledby="acc-profile">
      <h2 id="acc-profile" className="card-title"><Icon name="users" />{t('account.profile.title')}</h2>
      <Field label={t('common.name')}><input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={200} /></Field>
      <Field label={t('login.email')} hint={t('account.profile.emailHint')}><input value={me.user.email} readOnly disabled /></Field>
      <Field label={t('account.profile.language')} hint={t('account.profile.languageHint')}>
        <select value={locale} onChange={(e) => setLocale(e.target.value)}>
          <option value="">{t('account.profile.languageAuto', { current: i18n.language.toUpperCase() })}</option>
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </select>
      </Field>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn primary" disabled={busy || !dirty || !name.trim()}>{t('common.save')}</button>
      </div>
    </form>
  );
}

function PasswordCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api.post<{ revokedSessions: number }>('/auth/password/change', { currentPassword: cur, newPassword: pw });
      setCur('');
      setPw('');
      setPw2('');
      toast.show(r.revokedSessions ? t('account.password.changedOthers', { count: r.revokedSessions }) : t('account.password.changed'));
      await qc.invalidateQueries({ queryKey: ak.sessions });
    } catch (err) {
      setError(serverMessage(err) ?? t('errors.network'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card col" onSubmit={submit} aria-labelledby="acc-pw" noValidate>
      <h2 id="acc-pw" className="card-title"><Icon name="key" />{t('account.password.title')}</h2>
      <Field label={t('account.password.current')}>
        <input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />
      </Field>
      <PasswordFields value={pw} repeat={pw2} onChange={setPw} onRepeat={setPw2} />
      <div className="muted small">{t('account.password.othersHint')}</div>
      <div className="error" role="alert">{error}</div>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn primary" disabled={busy || !cur || !passwordOk(pw, pw2)}>{t('account.password.submit')}</button>
      </div>
    </form>
  );
}

function MfaCard({ me }: { me: Me }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: st, isLoading } = useMfaStatus();
  const [dialog, setDialog] = useState<null | 'setup' | 'codes' | 'disable'>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ak.mfa });
  return (
    <section className="card col" aria-labelledby="acc-mfa">
      <h2 id="acc-mfa" className="card-title"><Icon name="shield" />{t('account.mfa.title')}</h2>
      {isLoading || !st ? <Spinner /> : (
        <>
          <div className="row">
            {st.enabled
              ? <span className="pill ok" data-testid="mfa-state">{t('account.mfa.on')}</span>
              : <span className="pill neutral" data-testid="mfa-state">{t('account.mfa.off')}</span>}
            {st.enabled && st.confirmedAt && <span className="muted small">{t('account.mfa.since', { date: formatDateTime(st.confirmedAt, locale) })}</span>}
          </div>
          <div className="muted small">{t('account.mfa.text')}</div>
          {st.required && <div className="hint" role="note">{st.requiredBy === 'platform' ? t('account.mfa.requiredPlatform') : t('account.mfa.requiredTenant')}</div>}
          {!st.available && <div className="hint warn" role="note">{t('account.mfa.unavailable')}</div>}
          {st.enabled ? (
            <>
              <div className={`small ${st.recoveryCodesRemaining <= 3 ? 'warn-text' : 'muted'}`}>{t('account.mfa.codesLeft', { count: st.recoveryCodesRemaining })}</div>
              <div className="row">
                <button className="btn" onClick={() => setDialog('codes')}><Icon name="refresh" />{t('account.mfa.newCodes')}</button>
                <button className="btn ghost danger" disabled={st.required} title={st.required ? t('account.mfa.cannotDisable') : undefined} onClick={() => setDialog('disable')}>
                  {t('account.mfa.disable')}
                </button>
              </div>
            </>
          ) : (
            <div className="row">
              <button className="btn primary" disabled={!st.available} onClick={() => setDialog('setup')}><Icon name="shield" />{t('account.mfa.setup')}</button>
            </div>
          )}
        </>
      )}
      {dialog === 'setup' && (
        <Dialog wide title={t('account.mfa.setupTitle')} onClose={() => setDialog(null)}>
          <MfaSetupWizard email={me.user.email} onCancel={() => setDialog(null)} onFinished={(next) => {
            qc.setQueryData(qk.me, next);
            setDialog(null);
            void refresh();
            toast.show(t('account.mfa.enabled'));
          }} />
        </Dialog>
      )}
      {dialog === 'codes' && <NewCodesDialog email={me.user.email} onClose={() => { setDialog(null); void refresh(); }} />}
      {dialog === 'disable' && <DisableDialog onClose={(done) => {
        setDialog(null);
        void refresh();
        if (done) toast.show(t('account.mfa.disabled'));
      }} />}
    </section>
  );
}

function NewCodesDialog({ email, onClose }: { email: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      setCodes((await api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', { code })).recoveryCodes);
    } catch (err) {
      setError(serverMessage(err) ?? t('errors.network'));
      setCode('');
    }
  };
  return (
    <Dialog title={t('account.mfa.newCodes')} onClose={onClose}>
      {codes ? <RecoveryCodes codes={codes} email={email} onDone={onClose} doneLabel={t('common.close')} /> : (
        <form className="col" onSubmit={submit}>
          <div className="muted small">{t('account.mfa.newCodesText')}</div>
          <CodeInput value={code} onChange={setCode} label={t('account.mfa.code')} />
          <div className="error" role="alert">{error}</div>
          <div className="actions">
            <button type="button" className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
            <button className="btn primary" disabled={code.length !== 6}>{t('account.mfa.newCodesSubmit')}</button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function DisableDialog({ onClose }: { onClose: (done: boolean) => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await disableMfa(password, code.trim());
      onClose(true);
    } catch (err) {
      setError(serverMessage(err) ?? t('errors.network'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={t('account.mfa.disableTitle')} onClose={() => onClose(false)}>
      <form className="col" onSubmit={submit}>
        <div className="hint warn">{t('account.mfa.disableText')}</div>
        <Field label={t('login.password')}><input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        <Field label={t('account.mfa.codeOrRecovery')}>
          <input className="mono" inputMode="text" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
        </Field>
        <div className="error" role="alert">{error}</div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={() => onClose(false)}>{t('common.cancel')}</button>
          <button className="btn danger solid" disabled={busy || !password || code.trim().length < 6}>{t('account.mfa.disable')}</button>
        </div>
      </form>
    </Dialog>
  );
}

function SessionsCard() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: sessions, isLoading } = useSessions();
  const others = (sessions ?? []).filter((s) => !s.current).length;
  const end = async (s: SessionRow) => {
    try {
      await api.del(`/auth/sessions/${s.id}`);
      await qc.invalidateQueries({ queryKey: ak.sessions });
      toast.show(t('account.sessions.ended'));
    } catch (e) {
      toast.error(e);
    }
  };
  const endOthers = async () => {
    try {
      const r = await api.del<{ revoked: number }>('/auth/sessions');
      await qc.invalidateQueries({ queryKey: ak.sessions });
      toast.show(t('account.sessions.endedOthers', { count: r.revoked }));
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <section className="card col account-wide" aria-labelledby="acc-sess">
      <div className="row">
        <h2 id="acc-sess" className="card-title grow"><Icon name="screen" />{t('account.sessions.title')}</h2>
        <button className="btn sm" disabled={!others} onClick={() => void endOthers()}><Icon name="logout" />{t('account.sessions.endOthers')}</button>
      </div>
      <div className="muted small">{t('account.sessions.text')}</div>
      {isLoading ? <Spinner /> : (
        <div className="scroll">
          <table className="list" data-testid="sessions">
            <thead><tr><th>{t('account.sessions.device')}</th><th>{t('account.sessions.ip')}</th><th>{t('account.sessions.since')}</th><th>{t('account.sessions.last')}</th><th /></tr></thead>
            <tbody>
              {(sessions ?? []).map((s) => (
                <tr key={s.id}>
                  <td><b>{deviceLabel(s.userAgent)}</b>{s.supportTenant && <div className="muted small">{t('account.sessions.support', { tenant: s.supportTenant })}</div>}</td>
                  <td className="mono small">{s.ip ?? '–'}</td>
                  <td className="muted">{formatDateTime(s.createdAt, locale)}</td>
                  <td className="muted">{relativeTime(s.lastSeenAt, locale)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {s.current ? <span className="pill ok plain">{t('account.sessions.current')}</span>
                      : <button className="btn sm ghost" onClick={() => void end(s)}>{t('shell.logout')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
